import React, { Component } from 'react';
import styled from 'styled-components';
import {
  trackAnalytics,
  attachLocale,
  isSlug,
  makeExternalUrl,
  resolveAccessibleLabel,
  screenReaderTextReplace,
} from '../../../../lib/utils';
import {
  DOM_AD_SMALL,
  DATA_TRACK_ROLE_CONTAINER,
  DL_AD_BANNER,
  DATA_TRACK_TYPE_CTA,
} from '../../../../lib/constants';
import {
  addItemsToDataLayer,
  dataLayerIdGenerator,
  dataLayerIdGeneratorNewItem,
} from '../../../../lib/dataLayerUtils';
import PinterestAnalyticsTracker from '../shared/PinterestAnalyticsTracker';
import ArrowRight from '../../../svgs/ArrowRight';
import MilesOffer from '../shared/MilesOffer';
import DateDisplay from '../DateDisplay/DateDisplay';
import PromoBadge from '../PromoBadge/PromoBadge';
import { AdHeaderContainer } from './components';
import Button from '../Button/Button';
import ShopNowButton from '../Button/ShopNowButton';

// *** Desktop ***
const AdDiv = styled.div`
  width: ${p => p.theme.unit(45)};
  height: auto;
  margin-top: ${p => p.theme.unit(1.5)};
  margin-bottom: ${p => p.theme.unit(1.5)};
  border-radius: ${p => p.theme.unit(1)};
  border: 1px solid ${({ theme }) => theme.c.border.primary};
  ${({ theme }) => theme['elevation-shadow']['1']}
`;

const AdImg = styled.img`
  min-height: 170px;
  max-height: 212px;
  object-fit: contain;
  width: 100%;
  border-radius: 8px 8px 0 0;
`;

const AdShopContainerDiv = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AdShopImg = styled.img`
  max-height: 100%;
  max-width: 100%;
`;

const AdShopImgContainerDiv = styled.div`
  height: ${p => p.theme.unit(4)};
  display: flex;
  align-items: center;
`;

const AdBodyDiv = styled.div`
  padding: 8px 16px 8px 16px;
`;

const HeaderTextH2 = styled.h2`
  ${({ theme }) => theme.typographyRebrand.heading.xl}
  margin: 0;
  color: ${p => p.theme.c.text.primary};
  padding: ${p => p.theme.unit(2)} 0;
`;

const ScreenReaderOnlyH2 = styled.h2`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const DesktopHeaderTextH2 = styled(HeaderTextH2)`
  height: auto;
`;

const SubHeaderTextDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.body.md}
  height: ${p => p.theme.unit(11.25)};
  color: ${({ theme }) => theme.c.text.primary};
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
`;

const ButtonRowDiv = styled.div`
  padding-top: ${p => (p.exclusionsApply ? 0 : p.theme.unit(4.75))};
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

const AdSmallShopNowButton = styled(ShopNowButton)`
  padding-left: 24px;
  padding-right: 24px;
`;

const ExpiryHeightDiv = styled.div`
  height: ${p => p.theme.unit(3)};
  padding: 16px 0 8px 0;
`;

const ExclusionsTextDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.body.md}
  color: ${({ theme }) => theme.c.text.primary};
  padding-bottom: 16px;
`;

class AdSmall extends Component {
  constructor(props) {
    super(props);

    const itemSlug = this.props.slug ? this.props.slug : '';
    const dataLayerId = dataLayerIdGenerator(DL_AD_BANNER, itemSlug);
    this.state = {
      dataLayerId,
    };
  }

  // Analytics: Update dataLayer with new items
  componentDidMount() {
    const itemType = DL_AD_BANNER;
    const newItem = dataLayerIdGeneratorNewItem(
      itemType,
      this.props,
      this.state.dataLayerId
    );
    const items = [newItem];
    addItemsToDataLayer(items, itemType);
  }

  render() {
    const {
      imageUrl,
      name,
      slug,
      headerText,
      subHeaderText,
      primaryButtonText,
      primaryButtonDescriptiveText,
      primaryButtonUrl,
      secondaryButtonText,
      secondaryButtonDescriptiveText,
      secondaryButtonUrl,
      liveDate,
      expirationDate,
      expiryText,
      shop,
      shop: {
        baseMiles,
        amountPerBaseMiles,
        multiplier,
        siteWideMultiplier,
        mediumImageUrl,
        mediumImageAltText,
        slug: shopSlug,
        exclusionsApply,
        config: {
          singularMilesText,
          pluralMilesText,
          singularFlatRateMilesText,
          pluralFlatRateMilesText,
          milesBadgeText,
        },
      },
      tags,
      locale,
      pageStateModifiers,
      exclusionsApplyText,
      prepositionKey,
      linkTextPrepositions,
    } = this.props;

    const adImgUrl = makeExternalUrl(imageUrl);
    const shopImgUrl = makeExternalUrl(mediumImageUrl);
    const resolvedSecondaryUrl = isSlug(secondaryButtonUrl)
      ? attachLocale(secondaryButtonUrl, locale)
      : makeExternalUrl(secondaryButtonUrl);

    const trackSecondary = trackAnalytics(
      DOM_AD_SMALL,
      `${slug}-secondary`,
      resolvedSecondaryUrl,
      tags
    );

    return (
      <AdDiv
        data-track-role={DATA_TRACK_ROLE_CONTAINER}
        data-layer-source-id={this.state.dataLayerId}
      >
        <AdImg src={adImgUrl} alt="" />
        <AdBodyDiv>
          <AdShopContainerDiv>
            <AdShopImgContainerDiv>
              <AdShopImg src={shopImgUrl} alt={mediumImageAltText} />
            </AdShopImgContainerDiv>
            {multiplier ? (
              <PromoBadge
                extraMilesText={shop.config.extraMilesText}
                pluralFlatRateMilesText={pluralFlatRateMilesText}
              />
            ) : null}
          </AdShopContainerDiv>
          <AdHeaderContainer>
          {headerText ? (
            <div>
              <DesktopHeaderTextH2 aria-hidden="true">
                {headerText}
              </DesktopHeaderTextH2>
              <ScreenReaderOnlyH2>
                {screenReaderTextReplace(headerText, locale)}
              </ScreenReaderOnlyH2>
            </div>
          ) : (
            <MilesOffer
              className="miles-offer-extra-padding ad-small"
              singularMilesText={singularMilesText}
              pluralMilesText={pluralMilesText}
              baseMiles={baseMiles}
              multiplier={multiplier}
              siteWideMultiplier={siteWideMultiplier}
              singularFlatRateMilesText={singularFlatRateMilesText}
              pluralFlatRateMilesText={pluralFlatRateMilesText}
              milesBadgeText={milesBadgeText}
              amountPerBaseMiles={amountPerBaseMiles}
            />
          )}
          </AdHeaderContainer>
          <SubHeaderTextDiv>{subHeaderText}</SubHeaderTextDiv>
          <ExpiryHeightDiv>
            {(multiplier || siteWideMultiplier || expirationDate) && (
              <DateDisplay
                preText={expiryText}
                dates={[
                  multiplier && multiplier.endDate,
                  siteWideMultiplier && siteWideMultiplier.endDate,
                  expirationDate,
                ]}
                locale={locale}
              />
            )}
          </ExpiryHeightDiv>
          {exclusionsApply && (
            <ExclusionsTextDiv>{exclusionsApplyText}</ExclusionsTextDiv>
          )}
          <ButtonRowDiv exclusionsApply={exclusionsApply}>
            <PinterestAnalyticsTracker
              leadName={primaryButtonText}
              shopSlug={shopSlug}
            >
              <AdSmallShopNowButton
                variant="secondary"
                size="medium"
                theme="light"
                buttonText={primaryButtonText}
                dataLayerId={this.state.dataLayerId}
                accessibleLabel={resolveAccessibleLabel(
                  primaryButtonDescriptiveText,
                  shop,
                  linkTextPrepositions,
                  'at1',
                  primaryButtonText,
                  screenReaderTextReplace(headerText, locale),
                  prepositionKey || 'and'
                )}
                // Resolve interstitial URL params
                shopSlug={shopSlug}
                adSlug={slug}
                locale={locale}
                // Track analytics params
                trackAnalytics={(redirectUrl) => trackAnalytics(
                  DOM_AD_SMALL,
                  `${slug}-primary`,
                  redirectUrl,
                  tags
                )}
              />
            </PinterestAnalyticsTracker>
            <PinterestAnalyticsTracker
              leadName={secondaryButtonText}
              shopSlug={shopSlug}
            >
              <Button
                variant="tertiary"
                buttonText={secondaryButtonText}
                dataTrackType={DATA_TRACK_TYPE_CTA}
                dataLayerId={this.state.dataLayerId}
                renderRightIcon={({ className }) => <ArrowRight className={className} />}
                accessibleLabel={resolveAccessibleLabel(
                  secondaryButtonDescriptiveText,
                  shop,
                  linkTextPrepositions,
                  'for',
                  secondaryButtonText
                )}
                {...trackSecondary}
              />
            </PinterestAnalyticsTracker>
          </ButtonRowDiv>
        </AdBodyDiv>
      </AdDiv>
    );
  }
}

export default AdSmall;
